import React, { FC, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useAppContext } from '../../context/AppContext';
import { ROUTES } from '../../routes/routes';

export interface BreadcrumbsProps {
  [x: string]: any;
}

interface BreadcrumbItem {
  path: string;
  label: string;
  parentLabel: string;
}

// Helper function to match dynamic route paths like /categories/edit/:categoryId or /products/edit/:productId
const dynamicRouteMatch = (route: string, path: string) => {
  const routeSegments = route.split('/');
  const pathSegments = path.split('/');
  
  if (routeSegments.length !== pathSegments.length) {
    return false;
  }

  for (let i = 0; i < routeSegments.length; i++) {
    // Allow matching dynamic segments (like :categoryId or :productId)
    if (routeSegments[i].startsWith(':')) {
      continue;
    }

    if (routeSegments[i] !== pathSegments[i]) {
      return false;
    }
  }
  return true;
};

// Function to generate breadcrumbs based on the current path
const generateBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
  const breadcrumbArray = pathname.split('/').filter(Boolean);

  return breadcrumbArray.map((path, index) => {
    const fullPath = `/${breadcrumbArray.slice(0, index + 1).join('/')}`;
    let label = path;

    // Try to find a route with dynamic matching
    const route = Object.values(ROUTES).find((route) => dynamicRouteMatch(route, fullPath));
    
    // Handle dynamic parameters like :categoryId or :productId
    if (route) {
      // Check for dynamic segments (like categoryId, productId, etc.) and replace them with labels or values
      label = route || path;
    }

    const parentLabel = index > 0
      ? breadcrumbArray.slice(0, index).map((seg) => seg).join('/') // Join previous segments
      : '';

    return {
      path: fullPath,
      label: label,
      parentLabel: parentLabel,
    };
  });
};

const Breadcrumbs: FC<BreadcrumbsProps> = ({ className, ...restProps }) => {
  const breadcrumbs = useSelector((state: RootState) => state.breadcrumbs.breadcrumbs);

  const location = useLocation();
  const navigate = useNavigate();
  const { breadcrumbsFromPath, updateBreadcrumbsFromPath } = useAppContext();

  useEffect(() => {
    const newBreadcrumbs = generateBreadcrumbs(location.pathname);
    updateBreadcrumbsFromPath(newBreadcrumbs);
  }, [location.pathname]);

  return (
    <div className="flex items-start gap-2">
      <div className="flex flex-col items-start gap-2">
        <div className="flex flex-wrap">
          {breadcrumbs.map((breadcrumb: any, index: number) => (
            <span className={`flex items-center text-light normal-case font-medium md:text-sm text-xs ${index === breadcrumbs.length - 1 ? 'text-primaryLight' : ''}`} key={breadcrumb.link}>
              <Link to={breadcrumb.link} className="hover:no-underline cursor-pointer" onClick={() => navigate(breadcrumb.link)}>
                {breadcrumb.label}
              </Link>
              {index < breadcrumbs.length - 1 && <span className="px-1 text-light">{' > '}</span>}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Breadcrumbs;
